import React, { useMemo, useState } from 'react';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Command, CommandInput, CommandEmpty, CommandGroup, CommandItem, CommandList } from '../ui/command';
import { NumberInput } from '../ui/number-input';
import { Button } from '../ui/button';
import { X, Plus, Scale } from 'lucide-react';
import { fmtMoney } from '../../lib/format';
import InitialsAvatar from '../InitialsAvatar';

const CURRENCY_SYMBOL = { GBP: '£', USD: '$', EUR: '€' };

/**
 * Multi-talent picker with per-talent split as a currency amount.
 * Values stored in `splits` are talent-take-home amounts in the deal's currency.
 * Reports use them proportionally, so if the sum doesn't exactly equal the talent
 * share pot we still distribute the actual talent-share pot by their ratio.
 *
 * Props:
 *  - talent: full list of talents [{ user_id, name, picture, ... }]
 *  - selectedIds: string[]
 *  - splits: { [talentId]: number }  // currency amount per talent
 *  - onChange(nextIds, nextSplits)
 *  - currency: 'GBP' | 'USD' | 'EUR'
 *  - talentSharePot: number — target sum (deal value × (1 - commission%))
 */
const TalentMultiSelect = ({ talent, selectedIds = [], splits = {}, onChange, currency = 'GBP', talentSharePot = 0 }) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const symbol = CURRENCY_SYMBOL[currency] || '£';
  const selectedSet = useMemo(() => new Set(selectedIds), [selectedIds]);
  const selectedTalents = useMemo(
    () => selectedIds.map((id) => talent.find((t) => t.user_id === id)).filter(Boolean),
    [selectedIds, talent]
  );
  const remaining = useMemo(
    () => talent.filter((t) => !selectedSet.has(t.user_id)),
    [talent, selectedSet]
  );

  const totalSplit = selectedIds.reduce((s, id) => s + (Number(splits[id]) || 0), 0);
  const diff = totalSplit - talentSharePot;
  // Balanced if within £0.50 of the talent share pot (or no target set)
  const balanced = selectedIds.length === 0 || talentSharePot === 0 || Math.abs(diff) < 0.5;

  const evenAmounts = (ids) => {
    if (!ids.length || !talentSharePot) return Object.fromEntries(ids.map((tid) => [tid, 0]));
    const even = Math.round((talentSharePot / ids.length) * 100) / 100;
    // Adjust the last one so the sum matches the pot exactly
    const map = Object.fromEntries(ids.map((tid) => [tid, even]));
    const sum = even * ids.length;
    const remainder = Math.round((talentSharePot - sum) * 100) / 100;
    if (remainder !== 0 && ids.length) {
      map[ids[ids.length - 1]] = Math.round((even + remainder) * 100) / 100;
    }
    return map;
  };

  const add = (id) => {
    if (selectedSet.has(id)) return;
    const nextIds = [...selectedIds, id];
    const nextSplits = evenAmounts(nextIds);
    onChange(nextIds, nextSplits);
    setQuery('');
    setOpen(false);
  };

  const remove = (id) => {
    const nextIds = selectedIds.filter((x) => x !== id);
    const nextSplits = nextIds.length ? evenAmounts(nextIds) : {};
    onChange(nextIds, nextSplits);
  };

  const setSplit = (id, val) => {
    const nextSplits = { ...splits, [id]: Number(val) || 0 };
    onChange(selectedIds, nextSplits);
  };

  const evenSplit = () => {
    if (!selectedIds.length) return;
    onChange(selectedIds, evenAmounts(selectedIds));
  };

  return (
    <div>
      <div className="space-y-2">
        {selectedTalents.length === 0 && (
          <div className="text-[11px] text-slate-400 italic">no talent selected — add one or more below</div>
        )}
        {selectedTalents.map((t) => (
          <div
            key={t.user_id}
            className="flex items-center gap-2 px-2.5 py-2 bg-white border border-slate-200 rounded-lg"
            data-testid={`talent-chip-${t.user_id}`}
          >
            <InitialsAvatar name={t.name} size={28} />
            <div className="flex-1 min-w-0">
              <div className="text-sm text-slate-800 truncate">{t.name}</div>
              <div className="text-[11px] text-slate-400 truncate">{t.handle || t.email}</div>
            </div>
            {selectedIds.length > 1 && (
              <div className="flex items-center gap-1">
                <span className="text-xs text-slate-500 w-3 text-right">{symbol}</span>
                <NumberInput
                  value={splits[t.user_id] ?? ''}
                  onChange={(v) => setSplit(t.user_id, v)}
                  className="w-24 h-8 text-xs"
                  placeholder="0"
                  data-testid={`talent-split-${t.user_id}`}
                />
              </div>
            )}
            <button
              type="button"
              onClick={() => remove(t.user_id)}
              className="p-1 text-slate-400 hover:text-red-600"
              data-testid={`remove-talent-${t.user_id}`}
              title="remove"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        ))}
      </div>

      <div className="mt-2 flex items-center gap-2 flex-wrap">
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <button
              type="button"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-dashed border-slate-300 rounded-lg text-xs text-slate-600 hover:border-pink-300 hover:text-pink-700"
              data-testid="add-talent-btn"
            >
              <Plus className="w-3.5 h-3.5" />
              add talent
            </button>
          </PopoverTrigger>
          <PopoverContent align="start" className="p-0 w-72">
            <Command>
              <CommandInput
                value={query}
                onValueChange={setQuery}
                placeholder="search talent…"
              />
              <CommandList>
                <CommandEmpty>no talent match</CommandEmpty>
                <CommandGroup>
                  {remaining.map((t) => (
                    <CommandItem key={t.user_id} onSelect={() => add(t.user_id)} value={`${t.name} ${t.email} ${t.handle || ''}`}>
                      <div className="flex items-center gap-2 min-w-0">
                        <InitialsAvatar name={t.name} size={24} />
                        <div className="flex-1 min-w-0">
                          <div className="text-sm text-slate-800 truncate">{t.name}</div>
                          <div className="text-[11px] text-slate-400 truncate">{t.handle || t.email}</div>
                        </div>
                      </div>
                    </CommandItem>
                  ))}
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>

        {selectedIds.length > 1 && (
          <Button type="button" variant="ghost" size="sm" onClick={evenSplit} className="h-8 px-2 text-xs text-slate-500 hover:text-slate-800">
            <Scale className="w-3.5 h-3.5 mr-1" /> even split
          </Button>
        )}

        {selectedIds.length > 1 && (
          <div
            className={`ml-auto text-[11px] font-medium ${balanced ? 'text-slate-500' : 'text-amber-600'}`}
            data-testid="split-total-indicator"
          >
            total {fmtMoney(totalSplit, currency)} of {fmtMoney(talentSharePot, currency)}
            {balanced ? '' : ` · ${diff > 0 ? '+' : ''}${fmtMoney(diff, currency)}`}
          </div>
        )}
      </div>
    </div>
  );
};

export default TalentMultiSelect;
